/**
 * Skill result renderer — Streamdown + mermaid diagrams + Monaco-editor code
 * blocks + table-rendered csv/json data blocks.
 *
 * Mirrors `ncoder-ai/apps/shell-web/src/pages/dev-agent/chat/DevAgentChatMessageContent.tsx`,
 * minus the chat-store-specific Apex actions (Add to Workspace / Compare) and
 * artifact markers — those depend on `useDevAgentStore` which doesn't exist
 * here. What's preserved:
 *   - `streamdown` for streaming-safe markdown.
 *   - `@streamdown/mermaid` for diagram rendering (with `mermaidScrub`).
 *   - `@streamdown/code` as the default-language fallback (shiki highlighting).
 *   - Custom Monaco renderer for code blocks — same languages the chat uses.
 *   - Custom data renderer for csv/tsv/json/yaml — copy + download.
 */
import { memo, useMemo } from "react";
import { MermaidScrollGuard } from "@/components/molecules/MermaidScrollGuard";
import { Streamdown, defaultRemarkPlugins } from "streamdown";
import { mermaid } from "@streamdown/mermaid";
import { code } from "@streamdown/code";
import "streamdown/styles.css";
import "./streamdownOverrides.css";
import { cn } from "@/lib/studio/utils";
import { fixMermaidUnquotedLabels } from "@/lib/studio/mermaidScrub";
import { buildSkillCodeRenderer } from "./SkillCodeRenderer";
import { buildSkillDataRenderer } from "./SkillDataRenderer";
import { CollapsibleMarkdownList } from "./CollapsibleMarkdownList";
import { ReleaseScanReport, parseReleaseScanSse } from "./ReleaseScanReport";
import { maybeMetadataLink } from "@/lib/studio/runs";

const BASE_PLUGINS = { mermaid, code };

// remark plugin: rewrite genuine `artifact://` link nodes to a hash form so
// Streamdown's URL hardening — which hard-blocks custom schemes (verified that
// NO Streamdown prop, incl. urlTransform/allowedLinkPrefixes/allowedProtocols,
// disables it) — doesn't replace them with a "[blocked]" placeholder. Hash URLs
// are always allowed by the hardener; the link components below strip the
// leading "#" to recover the artifact id and render the CitationChip/editor.
// AST-scoped (unlike a string replace) so it never rewrites text inside code
// or inline-code. Appended to defaultRemarkPlugins so gfm (tables!) is kept.
function remarkArtifactScheme() {
  return (tree: unknown) => {
    const walk = (node: unknown): void => {
      const n = node as { type?: string; url?: string; children?: unknown[] };
      if (n && n.type === "link" && typeof n.url === "string" && n.url.startsWith("artifact://")) {
        n.url = `#${n.url}`;
      }
      (n?.children || []).forEach(walk);
    };
    walk(tree);
  };
}
const REMARK_PLUGINS = [...Object.values(defaultRemarkPlugins), remarkArtifactScheme];

// Allow only safe link targets. Bare/relative/anchor/query links are in-app and
// safe; absolute links must be http/https/mailto. Everything else (javascript:,
// data:, vbscript:, file:, etc.) is treated as unsafe and rendered as inert text.
function isSafeHref(href: string): boolean {
  const h = href.trim();
  if (!h) return false;
  if (/^(?:[/#?.]|https?:|mailto:)/i.test(h)) return true;
  // A bare token with no scheme (e.g. an internal route fragment) is safe; a
  // value containing a scheme separator before any path char is not.
  return !/^[a-z][a-z0-9+.-]*:/i.test(h);
}

export interface SkillResultMarkdownProps {
  content: string;
  className?: string;
  isStreaming?: boolean;
  components?: Record<string, unknown>;
}

function SkillResultMarkdownInner({
  content,
  className,
  isStreaming = false,
  components,
}: SkillResultMarkdownProps) {
  const text = useMemo(() => {
    // The `artifact://` -> `#artifact://` rewrite is now done AST-side by the
    // remarkArtifactScheme plugin (see REMARK_PLUGINS), not a string replace.
    return fixMermaidUnquotedLabels(decodeEntities(content));
  }, [content]);
  const releaseScanReport = useMemo(() => parseReleaseScanSse(text), [text]);

  const plugins = useMemo(
    () => ({
      ...BASE_PLUGINS,
      renderers: [
        buildSkillCodeRenderer({ isStreaming }),
        buildSkillDataRenderer({ isStreaming }),
      ],
    }),
    [isStreaming]
  );
  const mergedComponents = useMemo(
    () => ({
      // Long enumerations collapse to their first 30 items behind a toggle — see
      // CollapsibleMarkdownList for why this is a head slice and why it stands down
      // while streaming. Declared BEFORE the caller's `components` spread so a
      // surface that needs its own list renderer can still take it back.
      ul: ({ children: kids, className: cls }: { children?: React.ReactNode; className?: string }) => (
        <CollapsibleMarkdownList className={cls} isStreaming={isStreaming}>
          {kids}
        </CollapsibleMarkdownList>
      ),
      ol: ({ children: kids, className: cls, start }: { children?: React.ReactNode; className?: string; start?: number }) => (
        <CollapsibleMarkdownList ordered start={start} className={cls} isStreaming={isStreaming}>
          {kids}
        </CollapsibleMarkdownList>
      ),
      a: ({ href, children, ...rest }: { href?: string; children?: React.ReactNode } & Record<string, unknown>) => {
        // Undo the `#artifact://` hash-rewrite (see `text` above). Artifact
        // citations have no navigable target in this default renderer (the
        // CitationChip interceptor is only wired in ResultColumn), so render
        // the label as plain text rather than a dead/blocked link.
        const deHashed = href && href.startsWith("#artifact://") ? href.slice(1) : href;
        if (deHashed && deHashed.startsWith("artifact://")) {
          return <span {...rest}>{children}</span>;
        }
        const next = deHashed ? maybeMetadataLink(deHashed) : deHashed;
        // Defense-in-depth: only render navigable links for safe schemes
        // (http/https/mailto) or in-app relative/anchor targets. Dangerous
        // schemes (javascript:, data:, vbscript:) are rendered as inert text so
        // a malicious LLM/artifact link can never execute — even if the
        // markdown hardener config changes upstream.
        if (next && !isSafeHref(next)) {
          return <span {...rest}>{children}</span>;
        }
        const external = Boolean(next && /^(?:https?:)?\/\//.test(next));
        return (
          <a
            {...rest}
            href={next}
            target={external ? "_blank" : (rest.target as string | undefined)}
            rel={external ? "noreferrer" : (rest.rel as string | undefined)}
          >
            {children}
          </a>
        );
      },
      ...(components || {}),
    }),
    [components, isStreaming]
  );

  if (releaseScanReport) {
    return (
      <div className={cn("chat-streamdown skill-streamdown", className?.trim() ? className : "text-foreground")}>
        <ReleaseScanReport report={releaseScanReport} />
      </div>
    );
  }

  return (
    <div className={cn("chat-streamdown skill-streamdown", className?.trim() ? className : "text-foreground")}>
      <MermaidScrollGuard>
        <Streamdown
          parseIncompleteMarkdown
          remarkPlugins={REMARK_PLUGINS as never}
          plugins={plugins as never}
          components={mergedComponents as never}
          controls={{ mermaid: false }}
          isAnimating={isStreaming}
          animated
        >
          {text}
        </Streamdown>
      </MermaidScrollGuard>
    </div>
  );
}

export const SkillResultMarkdown = memo(SkillResultMarkdownInner);
SkillResultMarkdown.displayName = "SkillResultMarkdown";

function decodeEntities(s: string): string {
  if (!s) return "";
  if (!/&(?:lt|gt|amp|quot|#39|#x27);/.test(s)) return s;
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&amp;/g, "&");
}
